import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '@/components/ui/Modal'
import { FormField } from '@/components/ui/FormField'
import { apiService } from '@/services/api'
import type { Client } from '@/types'

const editClientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email'),
  phoneNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other', 'prefer-not-to-say']).optional(),
  preferredContactMethod: z.enum(['email', 'sms', 'app'])
})

type EditClientFormData = z.infer<typeof editClientSchema>

interface EditClientModalProps {
  client: Client
  isOpen: boolean
  onClose: () => void
  onSave: (client: Client) => void
}

export function EditClientModal({ client, isOpen, onClose, onSave }: EditClientModalProps) {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<EditClientFormData>({
    resolver: zodResolver(editClientSchema),
    defaultValues: {
      name: client.name,
      email: client.email,
      phoneNumber: client.phoneNumber || '',
      dateOfBirth: client.dateOfBirth?.toISOString().split('T')[0] || '',
      gender: client.gender,
      preferredContactMethod: client.preferredContactMethod
    }
  })

  const handleSave = async (data: EditClientFormData) => {
    setIsLoading(true)
    
    try {
      // Prepare client data for API (matching backend UpdateClientSchema)
      const clientUpdateData = {
        firstName: data.name.split(' ')[0] || data.name,
        lastName: data.name.split(' ').slice(1).join(' ') || '',
        // email is typically not updateable in most systems for security
        notes: data.phoneNumber ? `Phone: ${data.phoneNumber}` : undefined
      }

      console.log('🚀 Updating client with data:', clientUpdateData)
      
      // Call real API
      const response = await apiService.updateClient(client.id, clientUpdateData)
      console.log('✅ Client updated successfully:', response)
      
      // Transform API response to match frontend Client type
      const apiClient = (response as any).data || response
      const updatedClient: Client = {
        ...client,
        name: data.name,
        email: data.email, // Keep original email since backend might not update it
        phoneNumber: data.phoneNumber || undefined,
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : undefined,
        gender: data.gender,
        preferredContactMethod: data.preferredContactMethod,
        updatedAt: apiClient.updatedAt ? new Date(apiClient.updatedAt) : new Date()
      }
      
      onSave(updatedClient)
      reset()
    } catch (error: any) {
      console.error('❌ Failed to update client:', error)
      console.error('Error details:', error.response?.data || error.message)
      // TODO: Show user-friendly error message
      alert(`Failed to update client: ${error.response?.data?.message || error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Client Information"
      size="lg"
    >
      <form onSubmit={handleSubmit(handleSave)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Full Name"
            htmlFor="name"
            required
            error={errors.name?.message}
          >
            <input
              id="name"
              type="text"
              className="coach-input"
              {...register('name')}
            />
          </FormField>

          <FormField
            label="Email Address"
            htmlFor="email"
            required
            error={errors.email?.message}
          >
            <input
              id="email"
              type="email"
              className="coach-input"
              {...register('email')}
            />
          </FormField>

          <FormField
            label="Phone Number"
            htmlFor="phoneNumber"
            error={errors.phoneNumber?.message}
          >
            <input
              id="phoneNumber"
              type="tel"
              className="coach-input"
              placeholder="+1-555-0123"
              {...register('phoneNumber')}
            />
          </FormField>

          <FormField
            label="Date of Birth"
            htmlFor="dateOfBirth"
            error={errors.dateOfBirth?.message}
          >
            <input
              id="dateOfBirth"
              type="date"
              className="coach-input"
              {...register('dateOfBirth')}
            />
          </FormField>

          <FormField
            label="Gender"
            htmlFor="gender"
            error={errors.gender?.message}
          >
            <select
              id="gender"
              className="coach-input"
              {...register('gender')}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </FormField>

          <FormField
            label="Preferred Contact Method"
            htmlFor="preferredContactMethod"
            required
            error={errors.preferredContactMethod?.message}
          >
            <select
              id="preferredContactMethod"
              className="coach-input"
              {...register('preferredContactMethod')}
            >
              <option value="app">In-App Messages</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </FormField>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="coach-button-secondary px-4 py-2"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="coach-button-primary px-4 py-2"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}